/**
 * Share Target Parsers - Extract structured data from shared content
 */

export interface ParsedContact {
  name?: string;
  email?: string;
  phone?: string;
  company?: string;
  jobTitle?: string;
  address?: string;
}

export interface ParsedURL {
  url: string;
  title?: string;
  description?: string;
}

export interface ParsedFile {
  file: File;
  name: string;
  type: string;
  size: number;
}

export interface ParsedText {
  text: string;
  hasEmail: boolean;
  hasPhone: boolean;
  hasURL: boolean;
  extractedEmail?: string;
  extractedPhone?: string;
  extractedURL?: string;
}

/**
 * Parse vCard (contact) data
 */
export function parseVCard(text: string): ParsedContact | null {
  if (!text.includes('BEGIN:VCARD')) {
    return null;
  }

  const contact: ParsedContact = {};
  const lines = text.split('\n');

  for (const line of lines) {
    const [key, value] = line.split(':').map(s => s.trim());
    
    if (key === 'FN') {
      contact.name = value;
    } else if (key.startsWith('EMAIL')) {
      contact.email = value;
    } else if (key.startsWith('TEL')) {
      contact.phone = value;
    } else if (key === 'ORG') {
      contact.company = value;
    } else if (key === 'TITLE') {
      contact.jobTitle = value;
    } else if (key.startsWith('ADR')) {
      // Parse address (format: ;;street;city;state;zip;country)
      const addressParts = value.split(';').filter(Boolean);
      contact.address = addressParts.join(', ');
    }
  }

  return Object.keys(contact).length > 0 ? contact : null;
}

/**
 * Parse URL and extract metadata
 */
export function parseURL(url: string, title?: string): ParsedURL {
  try {
    const urlObj = new URL(url);
    return {
      url,
      title: title || urlObj.hostname,
      description: `Link from ${urlObj.hostname}`
    };
  } catch {
    return { url, title };
  }
}

/**
 * Parse file and extract metadata
 */
export function parseFile(file: File): ParsedFile {
  return {
    file,
    name: file.name,
    type: file.type,
    size: file.size
  };
}

/**
 * Parse text and detect actionable content
 */
export function parseText(text: string): ParsedText {
  const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/;
  const phoneRegex = /\b(\+?1?[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/;
  const urlRegex = /https?:\/\/[^\s]+/;

  const emailMatch = text.match(emailRegex);
  const phoneMatch = text.match(phoneRegex);
  const urlMatch = text.match(urlRegex);

  return {
    text,
    hasEmail: !!emailMatch,
    hasPhone: !!phoneMatch,
    hasURL: !!urlMatch,
    extractedEmail: emailMatch?.[0],
    extractedPhone: phoneMatch?.[0],
    extractedURL: urlMatch?.[0]
  };
}

/**
 * Detect share type from content
 */
export function detectShareType(data: {
  text?: string;
  url?: string;
  files?: File[];
}): 'contact' | 'file' | 'url' | 'text' | 'unknown' {
  // Check for vCard
  if (data.text && data.text.includes('BEGIN:VCARD')) {
    return 'contact';
  }

  // Check for files
  if (data.files && data.files.length > 0) {
    const file = data.files[0];
    if (file.type.includes('vcard') || file.name.endsWith('.vcf')) {
      return 'contact';
    }
    return 'file';
  }

  // Check for URL
  if (data.url) {
    return 'url';
  }

  // Check for text
  if (data.text) {
    return 'text';
  }

  return 'unknown';
}
